import { Dispatch } from "react";
import { Progress } from "@skbkontur/extern-core-js/dist/cjs/src/common/progress";
import { CancellationToken } from "@skbkontur/extern-core-js/dist/cjs/src/common/cancel";
import { FileUploaderAttachedFile } from "@skbkontur/react-ui";

import {
    backendConversionErrorMessage,
    formatLoadingErrorText,
    getAcceptFilesErrorFromName,
    invalidFileErrorText,
    loadFileErrorText,
} from "../../../../Engine/Core/ModalManager/Modals/Utils/TextContent";
import { UploadFileStatuses } from "../../../../Engine/Core/ModalManager/Modals/Utils/Types";
import { noop } from "../../../../../../Common/TypingUtils";
import { CustomFile } from "../../../../Helpers/file";
import { UploadedFileInfo, UploadedFileStatus } from "../../../../Engine/ActionCreators/FufUploadActionCreator";
import { Upload } from "../../../Services/Contexts/ExternDriveClientContext";
import { BulkFiles } from "../../../../Engine/Externals/Attachments/IAttachmentsManager";
import { createAbsolute } from "../../../../../../Common/ModelPath/ModelPath";

import {
    Action,
    setFileUri,
    setGfv,
    setNdsTotalSum,
    setProgress,
    setStatus,
    checkUniqueGfv,
} from "./FufAndAttachmentsReducer";
import { FilePropertiesOptions } from "./FufAndAttachmentsUploadModal";
import { AttachmentNames, UnitItem } from "./FufAndAttachmentsUploadModalTypes";

export type GetProperties = (file: CustomFile, options: FilePropertiesOptions) => Promise<UploadedFileInfo>;

interface Task {
    dispatch: Dispatch<Action>;
    upload: Upload;
    file: FileUploaderAttachedFile;
    currentGfv: string;
    isFormInFormNds: boolean;
    getFileProperties: GetProperties;
}

export const createUploadTask = async ({
    dispatch,
    upload,
    file,
    currentGfv,
    isFormInFormNds,
    getFileProperties,
}: Task) => {
    const extensionError = getAcceptFilesErrorFromName(file.originalFile.name, ["xml", "xls", "xlsx"]);

    if (extensionError) {
        dispatch(setStatus({ status: UploadFileStatuses.error, id: file.id, errorMessage: extensionError }));
        return;
    }

    dispatch(setStatus({ status: UploadFileStatuses.progress, id: file.id }));
    const progress = new Progress();
    const cancellationToken = new CancellationToken(noop);

    try {
        const res = await progress.subscribe(
            upload({ file: file.originalFile, progress: progress, cancellationToken: cancellationToken }),
            percents => {
                dispatch(
                    setProgress({ id: file.id, progressText: percents ? `${Math.round(percents)}%` : "Подготовка" })
                );
            }
        );

        const uploadFile: CustomFile = {
            name: file.originalFile.name,
            size: file.originalFile.size,
            contentUri: res.url,
        };

        setProgress({ id: file.id, progressText: "Обработка" });

        const properties = await getFileProperties(uploadFile, {
            declarationGfv: currentGfv,
            isFormInFormNds: isFormInFormNds,
        });

        if (properties.uploadStatus === UploadedFileStatus.Unresolved) {
            dispatch(
                setStatus({
                    id: file.id,
                    status: UploadFileStatuses.error,
                    errorMessage: invalidFileErrorText,
                })
            );
            return;
        }

        if (properties.fileProperties === undefined) {
            dispatch(
                setStatus({
                    id: file.id,
                    status: UploadFileStatuses.error,
                    errorMessage: loadFileErrorText,
                })
            );
            return;
        }

        dispatch(setFileUri({ id: file.id, fileUri: properties.fileProperties?.fufUri }));
        dispatch(setNdsTotalSum({ id: file.id, ndsTotalSum: properties.fileProperties?.ndsTotalSum }));
        dispatch(setStatus({ id: file.id, status: UploadFileStatuses.success }));
        dispatch(setGfv({ id: file.id, gfv: properties.fileProperties?.gfv }));

        if (properties.fileProperties?.gfv === currentGfv) {
            dispatch(checkUniqueGfv({ gfv: currentGfv }));
        }
    } catch (error) {
        let errorMessageText = loadFileErrorText;

        if (error instanceof Error && error.message) {
            errorMessageText = error.message === backendConversionErrorMessage ? formatLoadingErrorText : error.message;
        }

        dispatch(
            setStatus({
                status: UploadFileStatuses.error,
                id: file.id,
                errorMessage: errorMessageText,
            })
        );
    }
};

interface CreateBulkAttachmentsFiles {
    attachmentNames: AttachmentNames;
    currentGfv: string;
    state: UnitItem[];
}

export const createBulkAttachmentsFiles = ({ attachmentNames, currentGfv, state }: CreateBulkAttachmentsFiles) => {
    const attachmentGfvs = Object.keys(attachmentNames).filter(gfv => gfv !== currentGfv);
    const attachmentByGfv = Object.groupBy(
        state.filter(item => attachmentGfvs.includes(item.gfv) && item.status === UploadFileStatuses.success),
        ({ gfv }) => gfv
    );

    const bulkAttachmentsFiles: BulkFiles[] = [];

    for (const [gfv, attachments] of Object.entries(attachmentByGfv)) {
        const files = attachments?.map(item => item.file);
        const bindingPath = attachmentNames[gfv]?.bindingPath;

        if (!bindingPath || !files?.length) {
            continue;
        }

        bulkAttachmentsFiles.push({
            bindingPath: createAbsolute(bindingPath.toString()),
            files: files,
            gfv: gfv,
        });
    }

    return bulkAttachmentsFiles;
};
